import queries from '@apis/queries';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import TextUnderline from '@components/TextUnderline';
import colors from '@constants/colors';
import ReceiptIcon from '@icons/Receipt';
import RefreshIcon from '@icons/Refresh';
import { OrderLogResponse } from '@models/order/orderLog';
import { Link } from '@router/Link';
import { pressActionEffect } from '@styles/mixin';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

interface Props {
    orderLog: OrderLogResponse;
}

function OrderInfo({ orderLog }:Props) {
    const { t } = useTranslation('orderLog');
    const [refreshing, setRefreshing] = useState(false);
    const {
        createdAt, orderId, orderNo, status, shopName, shopOrderNo, displayOrderNo,
    } = orderLog;
    const queryClient = useQueryClient();
    const handleRefresh = () => {
        queryClient.invalidateQueries({
            queryKey: queries.orderLog.detail._def,
        });
        setRefreshing(true);
        const timeout = setTimeout(() => {
            setRefreshing(false);
        }, 201);
        return () => clearTimeout(timeout);
    };
    return <Column fullWidth as='section' align='center'
        className={styles.container} >
        <TextUnderline>
            <Text size='xl' fontWeight='bold'>
                {t('info.orderNumber', {
                    number: (displayOrderNo || `${shopOrderNo}`).padStart(4, '0'),
                })}
            </Text>
        </TextUnderline>
        <Text color={colors.gray6} size='lg' className={styles.date}>
            {dayjs(createdAt).format('YYYY.MM.DD HH:mm')}
        </Text>
        <Text size='3xl' fontWeight='bold' className={styles.comment}>
            {shopName}
        </Text>
        <Text size='3xl' >
            {status === 'CANCELLED' ? t('info.cancelled')
                : t('info.completed')}
        </Text>
        <Link activityName='OrderReceiptActivity'
            activityParams={{ orderId }}>
            <button className={styles.receipt} css={pressActionEffect}>
                <ReceiptIcon size={30} color={colors.primary} />
                <Text color={colors.primary} fontWeight='medium'>{t('info.receipt')}</Text>
            </button>
        </Link>
        {status !== 'CANCELLED' && (
            <Row className={styles.updateButton}
                onClick={handleRefresh} >
                <RefreshIcon size={30}
                    color={colors.gray6}
                    className={`${styles.updateIcon} ${refreshing && styles.animate}`} />
                <div className={styles.update}>
                    <Text color={colors.gray6}
                        className={styles.updateText}
                        fontWeight='bold'>
                        {t('info.updateStatus')}
                    </Text>
                </div>
            </Row>
        )}
    </Column>;
}

export default OrderInfo;
